import { useEffect, useState } from "react";
import { useToken, useUser } from "@packsize/authentication";
import { useFeatureFlags } from "@packsize/feature-flags";
import { type Role } from "@packsize/identity-api-sdk";
import { useToast } from "@packsize/toast";
import { AxiosError } from "axios";
import { useIntl } from "react-intl";

import { useCreateRole, useUpdateRole } from "@/hooks/roles";
import {
	allAdministratorPermissions,
	allDIMPermissions,
	allOperatorPermissions,
	allReportingPermissions,
	type Permission,
} from "@/views/configuration/roles/role-utils";

import {
	RoleConfigurationModalTemplate,
	type RoleConfigurationFormValues,
	type RolePermissionOptions,
} from "./role-configuration-modal.template";

export interface RoleConfigurationModalProps {
	readonly open: boolean;
	readonly role?: Role;
	readonly readOnly?: boolean;
	readonly onClose: () => void;
}

const getDefaultErrorMessage = (intl: ReturnType<typeof useIntl>) =>
	intl.formatMessage({ id: "Unable to save role", defaultMessage: "Unable to save role" });

const getErrorMessage = (error: unknown, intl: ReturnType<typeof useIntl>): string => {
	if (error instanceof AxiosError && error.status === 409) {
		return intl.formatMessage({
			id: "Unable to save role. Role already exists with this name",
			defaultMessage: "Unable to save role. Role already exists with this name",
		});
	}

	return getDefaultErrorMessage(intl);
};

export const RoleConfigurationModal = ({ open, role, readOnly = false, onClose }: RoleConfigurationModalProps) => {
	const intl = useIntl();
	const { decodedToken } = useToken();
	const { hasLicense } = useUser();
	const { isFeatureFlagEnabled } = useFeatureFlags();
	const { toastSuccess } = useToast();
	const [errorMessage, setErrorMessage] = useState<string>();
	const createRoleMutation = useCreateRole();
	const updateRoleMutation = useUpdateRole();

	useEffect(() => {
		if (open === true) {
			setErrorMessage(undefined);
		}
	}, [open]);

	const hasPermissionLicense = (permission: Permission): boolean => {
		if (permission.license === undefined) {
			return true;
		}

		return (
			hasLicense(permission.license.productCategory, permission.license.tier, permission.license.featureFlag) ?? false
		);
	};

	const hasFeatureFlag = (permission: Permission): boolean => {
		if (permission.featureFlag === undefined) {
			return true;
		}

		return isFeatureFlagEnabled(permission.featureFlag);
	};

	const availablePermissions: RolePermissionOptions = {
		Operator: allOperatorPermissions.filter(hasPermissionLicense).filter(hasFeatureFlag),
		Administrator: allAdministratorPermissions
			.filter((permission) => {
				return (
					permission.name !== "manageDimensioningConfiguration" && permission.name !== "manageDimensioningProducts"
				);
			})
			.filter(hasPermissionLicense)
			.filter(hasFeatureFlag),
		Reporting: allReportingPermissions.filter(hasPermissionLicense).filter(hasFeatureFlag),
		DIM: allDIMPermissions,
	};

	const handleSubmit = async (data: RoleConfigurationFormValues): Promise<void> => {
		setErrorMessage(undefined);

		const isUpdate = role?.id !== undefined;
		const roleData: Role = {
			id: role?.id ?? "",
			companyId: role?.companyId,
			name: data.name,
			tenantId: decodedToken?.tid ?? role?.tenantId ?? "",
			permissions: data.permissions,
		};

		try {
			const response = isUpdate
				? await updateRoleMutation.mutateAsync(roleData)
				: await createRoleMutation.mutateAsync(roleData);

			if (response.status === 409 || response.success === false) {
				setErrorMessage(
					response.status === 409
						? intl.formatMessage({
								id: "Unable to save role. Role already exists with this name",
								defaultMessage: "Unable to save role. Role already exists with this name",
							})
						: getDefaultErrorMessage(intl),
				);
				return;
			}

			toastSuccess({
				message: isUpdate
					? intl.formatMessage(
							{
								id: "Role {name} updated successfully",
								defaultMessage: "Role {name} updated successfully",
							},
							{ name: data.name },
						)
					: intl.formatMessage(
							{
								id: "Role {name} created successfully",
								defaultMessage: "Role {name} created successfully",
							},
							{ name: data.name },
						),
			});
			onClose();
		} catch (error: unknown) {
			setErrorMessage(getErrorMessage(error, intl));
		}
	};

	return (
		<RoleConfigurationModalTemplate
			open={open}
			role={role}
			readOnly={readOnly}
			availablePermissions={availablePermissions}
			errorMessage={errorMessage}
			onSubmit={handleSubmit}
			onClose={onClose}
		/>
	);
};
